import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import { MuiMarkdown } from 'mui-markdown'
import MotherThinking from 'src/v3/components/mother-thinking'

import { useAppSelector } from 'src/store/hooks'
import { selectFOAMfratInstructors } from 'src/v3/content-pipeline/instructor.slice'
import axios, { streamRequest } from 'src/utilities/http-client'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'

type InstructorContent = {
  events: { id: string; name: string; description: string | null }[]
  courses: { id: string; name: string }[]
}

type Step = 'configure' | 'loading' | 'result'

type Props = {
  open: boolean
  onClose: () => void
  initialInstructorID?: string
}

export default function SentimentAnalysisModal({
  open,
  onClose,
  initialInstructorID,
}: Props): ReactElement {
  let instructors = useAppSelector(selectFOAMfratInstructors)

  let [selectedInstructorId, setSelectedInstructorId] = useState(
    initialInstructorID ?? '',
  )

  useEffect(() => {
    if (open) setSelectedInstructorId(initialInstructorID ?? '')
  }, [open, initialInstructorID])
  let [content, setContent] = useState<InstructorContent | null>(null)
  let [contentLoading, setContentLoading] = useState(false)
  let [activeTab, setActiveTab] = useState<'events' | 'courses'>('events')
  let [selectedEventId, setSelectedEventId] = useState('')
  let [selectedCourseId, setSelectedCourseId] = useState('')
  let [embellishment, setEmbellishment] = useState('')
  let [step, setStep] = useState<Step>('configure')
  let [result, setResult] = useState<string | null>(null)
  let [errorMsg, setErrorMsg] = useState<string | null>(null)

  let selectedInstructor = instructors.find(
    (i) => i.id === selectedInstructorId,
  )

  // Load instructor content when instructor changes
  useEffect(() => {
    let controller = new AbortController()

    if (!selectedInstructorId) {
      setContent(null)
      setSelectedEventId('')
      setSelectedCourseId('')
      setActiveTab('events')
      setContentLoading(false)
    } else {
      setContentLoading(true)
      axios
        .get<InstructorContent>(
          `/api/pipeline/instructor/${selectedInstructorId}/content`,
          { signal: controller.signal },
        )
        .then((res) => {
          setContent(res.data)
          setSelectedEventId('')
          setSelectedCourseId('')
          if (res.data.events.length === 0 && res.data.courses.length > 0) {
            setActiveTab('courses')
          } else {
            setActiveTab('events')
          }
        })
        .catch((err) => {
          if (!axios.isCancel?.(err)) setContent(null)
        })
        .finally(() => setContentLoading(false))
    }

    return () => controller.abort()
  }, [selectedInstructorId])

  let handleReset = () => {
    setStep('configure')
    setResult(null)
    setErrorMsg(null)
    setSelectedInstructorId('')
    setContent(null)
    setSelectedEventId('')
    setSelectedCourseId('')
    setEmbellishment('')
    setActiveTab('events')
  }

  let handleClose = () => {
    handleReset()
    onClose()
  }

  let isEventMode = activeTab === 'events'
  let hasEvents = (content?.events.length ?? 0) > 0
  let hasCourses = (content?.courses.length ?? 0) > 0
  let selectedContentId = isEventMode ? selectedEventId : selectedCourseId
  let canRun = !!selectedInstructorId && !!selectedContentId

  let handleRunAnalysis = async () => {
    if (!canRun) return
    setStep('loading')
    setErrorMsg(null)

    let path = isEventMode
      ? `/api/pipeline/instructor/${selectedInstructorId}/sentiment/event/${selectedEventId}`
      : `/api/pipeline/instructor/${selectedInstructorId}/sentiment/course/${selectedCourseId}`

    try {
      let receivedFirstChunk = false

      await streamRequest(path, {
        body: embellishment.trim()
          ? { embellishment: embellishment.trim() }
          : {},
        onChunk: (_chunk, full) => {
          if (!receivedFirstChunk) {
            // eslint-disable-next-line fp/no-mutation
            receivedFirstChunk = true
            setStep('result')
          }
          setResult(full)
        },
      })
    } catch (err: any) {
      let msg =
        err?.response?.data?.error ??
        err?.response?.data?.message ??
        'An error occurred running the analysis.'
      setErrorMsg(msg)
      setStep('configure')
    }
  }

  let handleCopyMarkdown = () => {
    if (result) {
      navigator.clipboard.writeText(result)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={step === 'loading' ? undefined : handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="sentiment-analysis-title"
      slotProps={{ paper: modalPaperProps }}
    >
      <DialogTitle
        id="sentiment-analysis-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant="h6" component="span">
          Sentiment analysis
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedInstructor && (
            <Chip
              label={selectedInstructor.name}
              variant="outlined"
              size="small"
              sx={{ borderRadius: '16px' }}
            />
          )}
          {step !== 'loading' && (
            <IconButton
              aria-label="Close sentiment analysis"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {step === 'result' ? (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="body1">Analysis complete</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyMarkdown}
                >
                  Copy markdown
                </Button>
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={handleReset}
                >
                  Run another
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={markdownStyles}>
              <MuiMarkdown>{result ?? ''}</MuiMarkdown>
            </Box>
          </Box>
        ) : step === 'loading' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            <MotherThinking
              isThinking
              size="medium"
              currentMessage="MOTHER is reading survey responses…"
            />
          </Box>
        ) : (
          <Box>
            {errorMsg && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                }}
              >
                <Typography variant="body2">{errorMsg}</Typography>
              </Box>
            )}

            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500 }}>
              Instructor
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={selectedInstructorId}
                onChange={(e) => setSelectedInstructorId(e.target.value)}
                displayEmpty
                sx={modalInputSx}
                renderValue={(val) =>
                  val
                    ? (instructors.find((i) => i.id === val)?.name ?? val)
                    : 'Choose an instructor…'
                }
              >
                {instructors.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.credentials ? `${i.name} (${i.credentials})` : i.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedInstructorId && !contentLoading && content && (
              <>
                {(hasEvents || hasCourses) && (
                  <Box>
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => {
                        setActiveTab(v)
                        setSelectedEventId('')
                        setSelectedCourseId('')
                      }}
                      sx={{ mb: 2 }}
                    >
                      {hasEvents && <Tab label="Events" value="events" />}
                      {hasCourses && <Tab label="Courses" value="courses" />}
                    </Tabs>

                    {isEventMode && hasEvents && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.75, fontWeight: 500 }}
                        >
                          Event
                        </Typography>
                        <Select
                          value={selectedEventId}
                          onChange={(e) => setSelectedEventId(e.target.value)}
                          displayEmpty
                          sx={modalInputSx}
                          renderValue={(val) =>
                            val
                              ? (content.events.find((ev) => ev.id === val)
                                  ?.name ?? val)
                              : 'Select an event…'
                          }
                        >
                          {content.events.map((ev) => (
                            <MenuItem key={ev.id} value={ev.id}>
                              {ev.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {!isEventMode && hasCourses && (
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.75, fontWeight: 500 }}
                        >
                          Course
                        </Typography>
                        <Select
                          value={selectedCourseId}
                          onChange={(e) => setSelectedCourseId(e.target.value)}
                          displayEmpty
                          sx={modalInputSx}
                          renderValue={(val) =>
                            val
                              ? (content.courses.find((c) => c.id === val)
                                  ?.name ?? val)
                              : 'Select a course…'
                          }
                        >
                          {content.courses.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {selectedContentId && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ mb: 0.75, fontWeight: 500 }}
                        >
                          Focus directive
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          placeholder="Optional — e.g. focus on how I explain complex procedures"
                          value={embellishment}
                          onChange={(e) => setEmbellishment(e.target.value)}
                          sx={modalInputSx}
                        />
                        <Typography
                          color="text.secondary"
                          variant="caption"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Adds emphasis to the analysis prompt. Leave blank for
                          a standard report.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {!hasEvents && !hasCourses && (
                  <Typography color="text.secondary" variant="body2">
                    This instructor has no associated events or courses.
                  </Typography>
                )}
              </>
            )}

            {selectedInstructorId && contentLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box />
              <Button
                variant="contained"
                disabled={!canRun}
                onClick={handleRunAnalysis}
              >
                Run analysis
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
